/**
 * sonar-extractor.utils.ts
 *
 * Utilitários compartilhados por todos os componentes do módulo Sonar Extractor.
 * Centraliza: paleta de cores, opções de gráfico, builders de séries,
 * helpers de download, gerenciamento de tooltips e formatadores de data.
 */

import { PoChartSerie, PoChartType, PoSelectOption } from '@po-ui/ng-components';
import { ConsolidatedRow } from '../interfaces/consolidated-report';
import { DailyReportRow } from '../interfaces/reports';

// ─── Paleta de cores (padrão Totvs / Sonar Extractor) ────────────────────────

export const SONAR_PREMISE_COLORS: Readonly<Record<string, string>> = {
  total:    '#002233',
  resolve:  '#FF8800',
  resolve_or_reduce:    '#00C9EB',
};

/** Paleta usada em gráficos circulares (por projeto) */
export const SONAR_CHART_PALETTE: ReadonlyArray<string> = [
  '#00C9EB',
  '#FF8800',
  '#002233',
];

// ─── Opções de tipo de gráfico ────────────────────────────────────────────────

/** Lista completa de tipos de gráfico disponíveis */
export const SONAR_CHART_TYPE_OPTIONS: PoSelectOption[] = [
  { label: 'Área',    value: PoChartType.Area   },
  { label: 'Barras',  value: PoChartType.Bar    },
  { label: 'Colunas', value: PoChartType.Column },
  { label: 'Linha',   value: PoChartType.Line   },
  { label: 'Pizza',   value: PoChartType.Pie    },
  { label: 'Rosca',   value: PoChartType.Donut  },
];

/** Lista sem tipos circulares (indicada para séries temporais) */
export const SONAR_CHART_TYPE_OPTIONS_NO_CIRCULAR: PoSelectOption[] =
  SONAR_CHART_TYPE_OPTIONS.filter(
    opt => opt.value !== PoChartType.Pie && opt.value !== PoChartType.Donut
  );

// ─── Constantes de domínio ───────────────────────────────────────────────────

/**
 * Padrões de exclusão de projetos pré-definidos para novas extrações.
 * Suportam wildcard % no estilo SQL LIKE.
 * Altere aqui para refletir em todos os lugares que os utilizam.
 */
export const DEFAULT_EXCLUDE_PATTERNS = 'BSO%\n%RUSSIA%';

// ─── Datas padrão ─────────────────────────────────────────────────────────────

/** Retorna a data de `days` dias atrás (padrão: 30) no formato yyyy-MM-dd */
export function buildDefaultStart(days = 30): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split('T')[0];
}

/** Retorna a data de hoje no formato yyyy-MM-dd */
export function buildDefaultEnd(): string {
  return new Date().toISOString().split('T')[0];
}

// ─── Helpers de tipo circular ─────────────────────────────────────────────────

/** Retorna true se o tipo de gráfico é circular (Pizza ou Rosca) */
export function isCircularChartType(type: PoChartType): boolean {
  return type === PoChartType.Pie || type === PoChartType.Donut;
}

// ─── Builders de séries para gráfico diário ───────────────────────────────────

export interface DailyChartData {
  categories: string[];
  series: PoChartSerie[];
}

/**
 * Constrói categorias e séries para o gráfico de evolução diária
 * a partir de um array de DailyReportRow.
 */
export function buildDailyChartData(rows: DailyReportRow[]): DailyChartData {
  return {
    categories: rows.map(r => formatUtcDate(r.snapshot_date)),
    series: [
      { label: 'Zerar',                     data: rows.map(r => r.resolve),             color: SONAR_PREMISE_COLORS['resolve']  },
      { label: 'Zerar ou ao menos reduzir', data: rows.map(r => r.resolve_or_reduce),   color: SONAR_PREMISE_COLORS['resolve_or_reduce'] },
    ],
  };
}

/**
 * Gera séries agregadas (soma total por categoria) para tipos circulares
 * com base nos dados de DailyReportRow.
 */
export function buildCircularDailySeries(rows: DailyReportRow[]): PoChartSerie[] {
  type DailyKey             = 'issues_total' | 'resolve' | 'resolve_or_reduce';
  const cols: DailyKey[]    = ['issues_total', 'resolve', 'resolve_or_reduce'];
  const labels              = ['Total', 'Zerar', 'Zerar ou ao menos reduzir'];
  const colors              = [
    SONAR_PREMISE_COLORS['total'],
    SONAR_PREMISE_COLORS['resolve'],
    SONAR_PREMISE_COLORS['resolve_or_reduce'],
  ];
  return cols.map((col, i) => ({
    label: labels[i],
    data:  rows.reduce((s, r) => s + (r[col] as number), 0),
    color: colors[i],
  }));
}

// ─── Builders de séries para gráfico consolidado ──────────────────────────────

export interface ConsolidatedChartData {
  categories: string[];
  series: PoChartSerie[];
}

/**
 * Constrói categorias (projetos) e séries por severidade para o gráfico
 * consolidado a partir de um array de ConsolidatedRow.
 */
export function buildConsolidatedChartData(rows: ConsolidatedRow[]): ConsolidatedChartData {
  const projects = [...new Set(rows.map(r => r.project_name))];
  const sum = (field: keyof ConsolidatedRow, proj: string): number =>
    rows.filter(r => r.project_name === proj).reduce((s, r) => s + (r[field] as number), 0);

  return {
    categories: projects,
    series: [
      { label: 'Zerar',  data: projects.map(p => sum('resolve',  p)), color: SONAR_PREMISE_COLORS['resolve']  },
      { label: 'Zerar ou ao menos reduzir', data: projects.map(p => sum('resolve_or_reduce', p)), color: SONAR_PREMISE_COLORS['resolve_or_reduce'] },
    ],
  };
}

/**
 * Gera séries por projeto (soma de issues_total) para tipos circulares
 * com base nos dados de ConsolidatedRow.
 */
export function buildCircularConsolidatedSeries(rows: ConsolidatedRow[]): PoChartSerie[] {
  const projectMap = new Map<string, number>();
  rows.forEach(r => {
    projectMap.set(r.project_name, (projectMap.get(r.project_name) ?? 0) + r.issues_total);
  });
  return Array.from(projectMap.entries()).map(([label, total], i) => ({
    label,
    data: total,
    color: SONAR_CHART_PALETTE[i % SONAR_CHART_PALETTE.length],
  }));
}

// ─── Download helpers ─────────────────────────────────────────────────────────

/** Dispara o download de um Blob com o nome de arquivo informado */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href     = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Exporta o gráfico SVG de um container HTML como imagem PNG de alta resolução.
 * @param containerId ID do elemento HTML que contém o <svg>
 * @param filename    Nome do arquivo PNG a ser gerado
 * @param onWarning   Callback para exibir aviso quando não houver gráfico
 */
export function exportChartToPng(
  containerId: string,
  filename: string,
  onWarning: (msg: string) => void
): Promise<void> {
  const container = document.getElementById(containerId);
  const svgEl     = container?.querySelector('svg') as SVGSVGElement | null;

  if (!svgEl) {
    onWarning('Nenhum gráfico disponível para exportar.');
    return Promise.resolve();
  }

  return new Promise<void>((resolve, reject) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        try {
          const svg     = container!.querySelector('svg')!;
          const scale   = 2;
          const svgRect = svg.getBoundingClientRect();
          const width   = svg.clientWidth  || svgRect.width;
          const height  = svg.clientHeight || svgRect.height;

          // 1. Captura posição e estilo de cada elemento <text> na tela
          const textData = Array.from(svg.querySelectorAll('text')).map(t => {
            const rect  = t.getBoundingClientRect();
            const style = window.getComputedStyle(t);
            return {
              text:  t.textContent?.trim() || '',
              x:     rect.left - svgRect.left,
              y:     rect.top  - svgRect.top,
              font:  `${style.fontWeight || 'normal'} ${style.fontSize} ${style.fontFamily}`,
              color: style.fill && style.fill !== 'none' ? style.fill : (style.color || '#000000'),
            };
          });

          // 2. Clona o SVG removendo os textos (evita dupla renderização)
          const clonedSvg = svg.cloneNode(true) as SVGElement;
          clonedSvg.querySelectorAll('text').forEach(t => t.remove());

          // 3. Serializa o clone sem textos
          let svgData = new XMLSerializer().serializeToString(clonedSvg);
          if (!svgData.match(/^<svg[^>]+xmlns="http\:\/\/www\.w3\.org\/2000\/svg"/)) {
            svgData = svgData.replace(/^<svg/, '<svg xmlns="http://www.w3.org/2000/svg"');
          }

          const blob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
          const url  = URL.createObjectURL(blob);
          const img  = new Image();

          img.onload = () => {
            const canvas    = document.createElement('canvas');
            canvas.width    = width  * scale;
            canvas.height   = height * scale;
            const ctx       = canvas.getContext('2d')!;

            // 4. Fundo branco + escala de alta resolução
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.scale(scale, scale);

            // 5. Pinta o gráfico (barras/linhas)
            ctx.drawImage(img, 0, 0);

            // 6. Sobrepõe os textos capturados com renderização nativa
            ctx.textBaseline = 'top';
            textData.forEach(item => {
              if (item.text) {
                ctx.font      = item.font;
                ctx.fillStyle = item.color;
                ctx.fillText(item.text, item.x, item.y);
              }
            });

            URL.revokeObjectURL(url);

            const link    = document.createElement('a');
            link.download = filename;
            link.href     = canvas.toDataURL('image/png');
            link.click();
            resolve();
          };

          img.onerror = reject;
          img.src     = url;
        } catch (err) {
          reject(err);
        }
      });
    });
  });
}

// ─── Gerenciamento de tooltips órfãos ─────────────────────────────────────────

/** Remove todos os tooltips órfãos do body */
export function clearOrphanTooltips(): void {
  document.querySelectorAll('.po-tooltip').forEach(el => el.remove());
}

/**
 * Inicia um MutationObserver que mantém apenas o último tooltip visível,
 * evitando acúmulo de tooltips órfãos ao trocar o tipo de gráfico.
 * Retorna o observer para que o componente possa desconectá-lo no ngOnDestroy.
 */
export function startTooltipObserver(): MutationObserver {
  let scheduled = false;
  const observer = new MutationObserver(mutations => {
    const hasNewTooltip = mutations.some(
      m => m.type === 'childList' &&
        Array.from(m.addedNodes).some(
          (n): n is Element => n instanceof Element && n.classList.contains('po-tooltip')
        )
    );
    if (!hasNewTooltip || scheduled) { return; }
    scheduled = true;
    // Adia a limpeza para não bloquear o handler de mutação
    requestAnimationFrame(() => {
      scheduled = false;
      const all = Array.from(document.querySelectorAll('.po-tooltip'));
      if (all.length > 1) {
        all.slice(0, -1).forEach(el => el.remove());
      }
    });
  });
  observer.observe(document.body, { childList: true });
  return observer;
}

// ─── Formatadores de data ─────────────────────────────────────────────────────

/**
 * Formata uma string de data/hora ISO para o formato local pt-BR.
 * Retorna '-' para valores inválidos ou vazios.
 */
export function formatDateTime(dateStr: string): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleString('pt-BR');
}

/**
 * Formata uma data UTC (sem conversão de fuso) para dd/MM/yyyy.
 * Utilizada para datas de snapshot que não devem ser afetadas pelo fuso local.
 */
export function formatUtcDate(dateStr: string): string {
  const d     = new Date(dateStr);
  const day   = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year  = d.getUTCFullYear();
  return `${day}/${month}/${year}`;
}
